import logger from '../lib/logger';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Phone, MessageCircle, Check, SkipForward, Plus,
  Clock, AlertTriangle, ChevronLeft, ChevronRight,
  MapPin, Calendar, PhoneCall, Users, Eye,
  CheckCircle, ArrowUpRight, Flame, CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call', icon: PhoneCall, color: 'text-green-600', bg: 'bg-green-50' },
  { value: 'site_visit', label: 'Site Visit', icon: MapPin, color: 'text-blue-600', bg: 'bg-blue-50' },
  { value: 'follow_up', label: 'Follow-up', icon: Phone, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { value: 'meeting', label: 'Meeting', icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
  { value: 'other', label: 'Other', icon: Calendar, color: 'text-slate-600', bg: 'bg-slate-50' },
];

const typeInfo = (type) => ACTIVITY_TYPES.find(a => a.value === type) || ACTIVITY_TYPES[5];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(d) { return d.toISOString().slice(0, 10); }
function isToday(dateStr) { return dateStr === fmtDate(new Date()); }
function isPast(dateStr) { return dateStr < fmtDate(new Date()); }

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [planner, setPlanner] = useState(null);
  const [upcoming, setUpcoming] = useState({});
  const [stats, setStats] = useState(null);
  const [actionData, setActionData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showReschedule, setShowReschedule] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [taskForm, setTaskForm] = useState({ title: '', task_type: 'call', due_date: '', priority: 'medium', notes: '' });

  // 14-day window calculation
  const plannerRange = useMemo(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - now.getDay() + 1 + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);
    const end = new Date(monday);
    end.setDate(monday.getDate() + 14);
    const days = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      days.push(fmtDate(d));
    }
    return { start: fmtDate(monday), end: fmtDate(end), days };
  }, [weekOffset]);

  const fetchPlanner = useCallback(async () => {
    try {
      const params = new URLSearchParams({ start: plannerRange.start, end: plannerRange.end });
      if (selectedAgent) params.append('agent_id', selectedAgent);
      const res = await api.get(`/dashboard/planner?${params}`);
      setPlanner(res.data);
    } catch (e) { logger.error('Planner fetch failed', e); }
  }, [plannerRange, selectedAgent]);

  const upcomingRange = useMemo(() => {
    const days = [];
    for (let i = 1; i <= 7; i++) { const d = new Date(); d.setDate(d.getDate() + i); days.push(fmtDate(d)); }
    return { start: days[0], end: days[6], days: days.slice(0, 6) };
  }, []);

  const fetchUpcoming = useCallback(async () => {
    try {
      const params = new URLSearchParams({ start: upcomingRange.start, end: upcomingRange.end });
      if (selectedAgent) params.append('agent_id', selectedAgent);
      const res = await api.get(`/dashboard/planner?${params}`);
      setUpcoming(res.data.days || {});
    } catch (e) { logger.error('Upcoming fetch failed', e); }
  }, [upcomingRange, selectedAgent]);

  const fetchDashboard = useCallback(async () => {
    try {
      const [statsRes, actionRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/dashboard/action-center'),
      ]);
      setStats(statsRes.data);
      setActionData(actionRes.data);
    } catch (e) { logger.error('Dashboard fetch error', e); }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchPlanner(); }, [fetchPlanner]);
  useEffect(() => { fetchUpcoming(); }, [fetchUpcoming]);
  const refreshAll = () => { fetchPlanner(); fetchDashboard(); fetchUpcoming(); };
  useEffect(() => {
    if (isAdmin) { api.get('/team/agents').then(r => setAgents(r.data)).catch(() => {}); }
  }, [isAdmin]);

  const handleComplete = async (taskId) => {
    try { await api.post(`/tasks/${taskId}/complete`); toast.success('Completed'); refreshAll(); }
    catch { toast.error('Failed'); }
  };
  const handleSnooze = async (taskId) => {
    try { await api.post(`/tasks/${taskId}/snooze`); toast.success('Snoozed to tomorrow'); refreshAll(); }
    catch { toast.error('Failed'); }
  };
  const handleReschedule = async () => {
    if (!showReschedule || !rescheduleDate) return;
    try {
      await api.post(`/tasks/${showReschedule}/reschedule`, { due_date: rescheduleDate });
      toast.success('Rescheduled'); setShowReschedule(null); setRescheduleDate('');
      refreshAll();
    } catch { toast.error('Failed to reschedule'); }
  };
  const handleAddTask = async () => {
    if (!taskForm.title || !taskForm.due_date) { toast.error('Title and date required'); return; }
    try {
      await api.post('/tasks/create', taskForm);
      toast.success('Activity created'); setShowAddTask(false);
      setTaskForm({ title: '', task_type: 'call', due_date: '', priority: 'medium', notes: '' });
      refreshAll();
    } catch { toast.error('Failed to create'); }
  };
  const handleWhatsApp = (phone, name) => {
    const clean = phone?.replace(/\D/g, '');
    const msg = `Hi ${name || ''}, reaching out from Aavaas Consultants.`;
    const isMobile = /Android|iPhone/i.test(navigator.userAgent);
    window.open(isMobile ? `https://wa.me/${clean}?text=${encodeURIComponent(msg)}` : `https://web.whatsapp.com/send?phone=${clean}&text=${encodeURIComponent(msg)}`, '_blank');
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Derived data
  const todayStr = fmtDate(new Date());
  const overdueTasks = planner?.overdue || [];
  const todayTasks = planner?.days?.[todayStr]?.tasks || [];
  const selectedDayTasks = selectedDay ? (planner?.days?.[selectedDay]?.tasks || []) : null;

  // Today-at-a-glance metrics
  const todayMetrics = useMemo(() => {
    const all = [...todayTasks, ...overdueTasks];
    const pending = all.filter(t => t.status === 'pending');
    const byType = {};
    for (const t of pending) { byType[t.task_type || 'other'] = (byType[t.task_type || 'other'] || 0) + 1; }
    return { total: pending.length, overdue: overdueTasks.filter(t => t.status === 'pending').length, byType };
  }, [todayTasks, overdueTasks]);

  if (!stats || !planner) return (
    <Layout>
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-[#B89B72] rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    </Layout>
  );

  // Render a single task row
  const TaskRow = ({ task, compact }) => {
    const ti = typeInfo(task.task_type);
    const Icon = ti.icon;
    const isPending = task.status === 'pending';
    const overdue = isPending && isPast(task.due_date?.slice(0, 10));
    return (
      <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors group ${!isPending ? 'opacity-50' : ''}`} data-testid={`task-${task.id}`}>
        <div className={`p-1.5 rounded-lg ${ti.bg} flex-shrink-0`}><Icon className={`w-3.5 h-3.5 ${ti.color}`} /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400 flex-wrap">
            {task.lead_name && <span className="text-slate-600 font-medium">{task.lead_name}</span>}
            {task.assigned_agent_name && !compact && <span>{task.assigned_agent_name}</span>}
            {overdue && <span className="text-amber-600 font-semibold">overdue</span>}
            {task.priority === 'high' && <span className="text-rose-500 font-semibold">high</span>}
          </div>
        </div>
        {isPending && (
          <div className="flex gap-0.5 flex-shrink-0 opacity-80 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
            {task.lead_phone && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-green-50" onClick={() => window.open(`tel:${task.lead_phone}`)}><Phone className="w-3.5 h-3.5 text-green-600" /></Button>}
            {task.lead_phone && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-emerald-50" onClick={() => handleWhatsApp(task.lead_phone, task.lead_name)}><MessageCircle className="w-3.5 h-3.5 text-emerald-500" /></Button>}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-emerald-50" onClick={() => handleComplete(task.id)}><Check className="w-3.5 h-3.5 text-emerald-600" /></Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-slate-100" onClick={() => { setShowReschedule(task.id); setRescheduleDate(task.due_date?.slice(0, 10) || ''); }}><SkipForward className="w-3.5 h-3.5 text-slate-400" /></Button>
          </div>
        )}
        {!isPending && <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
      </div>
    );
  };

  return (
    <Layout>
      <div className="aavaas-page" data-testid="dashboard-page">

        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
          <div>
            <p className="text-xs text-[#B89B72] font-semibold tracking-wide uppercase">{getGreeting()}</p>
            <h1 className="text-2xl font-bold text-slate-900 mt-0.5" data-testid="dashboard-title">{user?.name || 'Dashboard'}</h1>
            <p className="text-sm text-slate-500">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setTaskForm({ ...taskForm, due_date: todayStr }); setShowAddTask(true); }} data-testid="add-activity-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Activity
            </Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => navigate('/leads')} data-testid="add-lead-button">
              <Plus className="w-4 h-4 mr-1" /> Add Lead
            </Button>
          </div>
        </div>

        {/* ── TODAY AT-A-GLANCE ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Overdue', value: todayMetrics.overdue, color: 'text-amber-600', bg: 'bg-amber-50', icon: AlertTriangle },
            { label: 'Calls', value: todayMetrics.byType.call || 0, color: 'text-green-600', bg: 'bg-green-50', icon: PhoneCall },
            { label: 'Site Visits', value: todayMetrics.byType.site_visit || 0, color: 'text-blue-600', bg: 'bg-blue-50', icon: MapPin },
            { label: 'Follow-ups', value: todayMetrics.byType.follow_up || 0, color: 'text-cyan-600', bg: 'bg-cyan-50', icon: Phone },
            { label: 'WhatsApp', value: todayMetrics.byType.whatsapp || 0, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: MessageCircle },
            { label: 'New Leads', value: actionData?.counts?.new_today || 0, color: 'text-indigo-600', bg: 'bg-indigo-50', icon: Flame },
          ].map((m, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-100 p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-lg ${m.bg}`}><m.icon className={`w-3.5 h-3.5 ${m.color}`} /></div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{m.label}</span>
              </div>
              <p className={`text-2xl font-bold ${m.color} tracking-tight`}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* ── TEAM FILTER (Admin) ── */}
        {isAdmin && agents.length > 0 && (
          <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
            <button
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${!selectedAgent ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
              onClick={() => setSelectedAgent(null)}
            >All Team</button>
            {agents.filter(a => a.role === 'sales_rep' && !a.name?.startsWith('TEST')).map(a => (
              <button
                key={a.id}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedAgent === a.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
                onClick={() => setSelectedAgent(a.id)}
              >{a.name?.split(' ')[0]}</button>
            ))}
          </div>
        )}

        {/* ── 14-DAY PLANNER ── */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-900">14-Day Planner</h2>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setWeekOffset(w => w - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setWeekOffset(0); setSelectedDay(null); }}>Today</Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setWeekOffset(w => w + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Week labels */}
          <div className="px-4 pt-3 pb-1 flex gap-1">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-1 text-center">{MONTH_NAMES[new Date(plannerRange.days[0]).getMonth()]} — Week 1</span>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-1 text-center">Week 2</span>
          </div>

          {/* Day grid */}
          <div className="px-3 pb-3">
            <div className="grid grid-cols-7 gap-1.5">
              {plannerRange.days.map((day) => {
                const d = new Date(day + 'T00:00:00');
                const dayData = planner.days?.[day];
                const pending = dayData?.pending || 0;
                const total = dayData?.total || 0;
                const today = isToday(day);
                const past = isPast(day) && !today;
                const selected = selectedDay === day;
                const types = dayData?.types || {};

                return (
                  <button
                    key={day}
                    className={`relative flex flex-col items-center p-2 rounded-lg transition-all text-center min-h-[68px]
                      ${selected ? 'bg-slate-900 text-white ring-2 ring-slate-900' : today ? 'bg-[#B89B72]/10 ring-1 ring-[#B89B72]/30' : past ? 'bg-slate-50 opacity-60' : 'bg-slate-50 hover:bg-slate-100'}
                    `}
                    onClick={() => setSelectedDay(selected ? null : day)}
                    data-testid={`planner-day-${day}`}
                  >
                    <span className={`text-[10px] font-medium ${selected ? 'text-slate-300' : 'text-slate-400'}`}>{DAY_NAMES[d.getDay()]}</span>
                    <span className={`text-lg font-bold leading-tight ${selected ? 'text-white' : today ? 'text-[#B89B72]' : 'text-slate-700'}`}>{d.getDate()}</span>
                    {pending > 0 && (
                      <div className="flex gap-0.5 mt-1">
                        {types.call > 0 && <span className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-green-300' : 'bg-green-500'}`} />}
                        {types.site_visit > 0 && <span className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-blue-300' : 'bg-blue-500'}`} />}
                        {(types.follow_up > 0 || types.whatsapp > 0) && <span className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-cyan-300' : 'bg-cyan-500'}`} />}
                        {types.meeting > 0 && <span className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-purple-300' : 'bg-purple-500'}`} />}
                      </div>
                    )}
                    {pending > 0 && <span className={`text-[10px] font-bold mt-0.5 ${selected ? 'text-slate-300' : 'text-slate-500'}`}>{pending}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected day detail */}
          {selectedDay && (
            <div className="border-t border-slate-100">
              <div className="px-4 py-2.5 flex items-center justify-between bg-slate-50/50">
                <span className="text-sm font-semibold text-slate-900">
                  {new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
                </span>
                <span className="text-xs text-slate-400">{selectedDayTasks?.filter(t => t.status === 'pending').length || 0} pending</span>
              </div>
              {(!selectedDayTasks || selectedDayTasks.length === 0) ? (
                <div className="p-6 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-200" />
                  <p className="text-sm text-slate-400">No activities scheduled</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => { setTaskForm({ ...taskForm, due_date: selectedDay }); setShowAddTask(true); }}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Activity
                  </Button>
                </div>
              ) : (
                <div className="max-h-[300px] overflow-y-auto">
                  {selectedDayTasks.sort((a, b) => (a.status === 'pending' ? 0 : 1) - (b.status === 'pending' ? 0 : 1)).map(task => (
                    <TaskRow key={task.id} task={task} compact={false} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── TODAY'S SCHEDULE + OVERDUE ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">

          {/* Today's Activities */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="today-activities">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-cyan-50 rounded-lg"><Calendar className="w-3.5 h-3.5 text-cyan-600" /></div>
                <h3 className="font-semibold text-sm text-slate-900">Today&apos;s Activities</h3>
              </div>
              <span className="text-[11px] font-bold text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full">{todayTasks.filter(t => t.status === 'pending').length}</span>
            </div>
            <div className="max-h-[350px] overflow-y-auto">
              {todayTasks.filter(t => t.status === 'pending').length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-200" />
                  <p className="text-sm text-slate-400">All caught up for today</p>
                </div>
              ) : todayTasks.filter(t => t.status === 'pending').map(task => (
                <TaskRow key={task.id} task={task} compact />
              ))}
            </div>
          </div>

          {/* Overdue */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="overdue-section">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-50 rounded-lg"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /></div>
                <h3 className="font-semibold text-sm text-slate-900">Overdue</h3>
              </div>
              <span className="text-[11px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">{overdueTasks.filter(t => t.status === 'pending').length}</span>
            </div>
            <div className="max-h-[350px] overflow-y-auto">
              {overdueTasks.filter(t => t.status === 'pending').length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-200" />
                  <p className="text-sm text-slate-400">No overdue activities</p>
                </div>
              ) : overdueTasks.filter(t => t.status === 'pending').map(task => (
                <TaskRow key={task.id} task={task} compact />
              ))}
            </div>
          </div>
        </div>

        {/* ── UPCOMING THIS WEEK ── */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden mb-6" data-testid="upcoming-week-section">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-50 rounded-lg"><CalendarDays className="w-3.5 h-3.5 text-indigo-600" /></div>
              <h3 className="font-semibold text-sm text-slate-900">Upcoming This Week</h3>
            </div>
            <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full" data-testid="upcoming-count">
              {upcomingRange.days.reduce((n, d) => n + (upcoming[d]?.tasks || []).filter(t => t.status === 'pending').length, 0)}
            </span>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {upcomingRange.days.every(d => !(upcoming[d]?.tasks || []).some(t => t.status === 'pending')) ? (
              <div className="p-8 text-center">
                <CalendarDays className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                <p className="text-sm text-slate-400">Nothing scheduled for the next 6 days</p>
              </div>
            ) : upcomingRange.days.map(day => {
              const pending = (upcoming[day]?.tasks || []).filter(t => t.status === 'pending');
              if (!pending.length) return null;
              const d = new Date(day + 'T00:00:00');
              return (
                <div key={day} data-testid={`upcoming-day-${day}`}>
                  <div className="px-4 py-1.5 bg-slate-50/70 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    {day === upcomingRange.start ? 'Tomorrow' : DAY_NAMES[d.getDay()]} · {d.getDate()} {MONTH_NAMES[d.getMonth()]}
                  </div>
                  {pending.map(task => <TaskRow key={task.id} task={task} compact />)}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── TEAM WORKLOAD (Admin) ── */}
        {isAdmin && planner.team_workload?.length > 0 && !selectedAgent && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 mb-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              Team Workload — Next 14 Days
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {planner.team_workload.map(w => (
                <button key={w.user_id} className="flex items-center gap-3 p-3 bg-slate-50/50 rounded-lg hover:bg-slate-100 transition-colors text-left" onClick={() => setSelectedAgent(w.user_id)}>
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-xs font-bold text-slate-600">
                    {(w.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{w.name}</p>
                    <p className="text-xs text-slate-500">{w.pending_tasks} pending tasks</p>
                  </div>
                  <Eye className="w-4 h-4 text-slate-300" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── NEW LEADS + HOT LEADS ── */}
        {actionData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {actionData.new_today.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-indigo-50 rounded-lg"><Flame className="w-3.5 h-3.5 text-indigo-600" /></div>
                    <h3 className="font-semibold text-sm text-slate-900">New Leads Today</h3>
                  </div>
                  <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{actionData.new_today.length}</span>
                </div>
                <div className="max-h-[250px] overflow-y-auto">
                  {actionData.new_today.slice(0, 8).map(lead => (
                    <div key={lead.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 group">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{lead.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{lead.phone}</p>
                      </div>
                      <div className="flex gap-0.5 opacity-80 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-green-50" onClick={() => window.open(`tel:${lead.phone}`)}><Phone className="w-3.5 h-3.5 text-green-600" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-emerald-50" onClick={() => handleWhatsApp(lead.phone, lead.name)}><MessageCircle className="w-3.5 h-3.5 text-emerald-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {actionData.not_picked.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-orange-50 rounded-lg"><Phone className="w-3.5 h-3.5 text-orange-500" /></div>
                    <h3 className="font-semibold text-sm text-slate-900">Not Picked</h3>
                  </div>
                  <span className="text-[11px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{actionData.not_picked.length}</span>
                </div>
                <div className="max-h-[250px] overflow-y-auto">
                  {actionData.not_picked.slice(0, 8).map(lead => (
                    <div key={lead.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 group">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{lead.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{lead.phone}</p>
                      </div>
                      <div className="flex gap-0.5 opacity-80 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-green-50" onClick={() => window.open(`tel:${lead.phone}`)}><Phone className="w-3.5 h-3.5 text-green-600" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-emerald-50" onClick={() => handleWhatsApp(lead.phone, lead.name)}><MessageCircle className="w-3.5 h-3.5 text-emerald-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── RESCHEDULE DIALOG ── */}
        <Dialog open={!!showReschedule} onOpenChange={() => setShowReschedule(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Reschedule Activity</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>New Date</Label>
                <Input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} className="mt-1" />
              </div>
              <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleReschedule}>Reschedule</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── ADD ACTIVITY DIALOG ── */}
        <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New Activity</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Title *</Label>
                <Input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="e.g., Call Sikander re: Aerocity 300" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={taskForm.task_type} onValueChange={v => setTaskForm({ ...taskForm, task_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={taskForm.priority} onValueChange={v => setTaskForm({ ...taskForm, priority: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={taskForm.notes} onChange={e => setTaskForm({ ...taskForm, notes: e.target.value })} placeholder="Optional notes" className="mt-1" />
              </div>
              <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleAddTask} data-testid="submit-activity">Create Activity</Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
};

export default Dashboard;
